import {
    getSessionTypeAnnualLimit,
    hasCompletedGuidanceOnboarding,
    sessionsCountingTowardAnnualLimit,
} from '@fruitful/guidance';
import { type GuidanceSession, GuidanceSessionType } from '@fruitful/types';
import { parseISO } from 'date-fns';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    CaptionsIcon,
    CheckboxWithLabel,
    CircleCheckIcon,
    ExternalLinkIcon,
    H6Emphasized,
    Icon,
    InfoButtonWithTooltip,
    XStack,
    YStack,
} from 'smoothie';

import {
    UrlLinkRenderer,
    ViewItemRenderer,
} from '../../common/components/GridCellRenderers';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import { type MembershipDetails } from '../../entities/entityTypes';
import { getMembershipProduct } from '../utils/membershipGuidanceSessionDisplayName';
import {
    type GroupedSessionRow,
    groupSessionRowsByMembershipYear,
} from '../utils/membershipYearSessionGrouping';
import { GuidanceSessionNotesModal } from './GuidanceSessionNotesModal';
import { GuidanceSessionStatusCell } from './GuidanceSessionStatusCell';
import { GuidanceSessionTranscriptModal } from './GuidanceSessionTranscriptModal';

export const CheckInSessionsTable = ({
    guidanceSessions,
    membershipDetails,
}: {
    guidanceSessions: GuidanceSession[];
    membershipDetails: MembershipDetails;
}) => {
    const [showUrls, setShowUrls] = useState(false);
    const [notesSession, setNotesSession] = useState<GuidanceSession | null>(
        null,
    );
    const [transcriptSession, setTranscriptSession] =
        useState<GuidanceSession | null>(null);

    const product = getMembershipProduct(membershipDetails);
    const annualLimit = getSessionTypeAnnualLimit(
        product,
        GuidanceSessionType.QUICK_CHECK_IN,
    );
    const hasNumericLimit = typeof annualLimit === 'number';

    const checkIns = useMemo(
        () =>
            guidanceSessions.filter(
                (session) =>
                    session.type === GuidanceSessionType.QUICK_CHECK_IN,
            ),
        [guidanceSessions],
    );

    const rows = useMemo(
        () =>
            groupSessionRowsByMembershipYear({
                membershipYearEndsAt: parseISO(
                    membershipDetails.membershipYearEndsAt ?? '',
                ),
                now: new Date(),
                sessions: checkIns,
                productDisplayName: product?.displayName ?? 'Unknown',
                sessionTypeAnnualLimit: annualLimit,
            }),
        [
            checkIns,
            membershipDetails.membershipYearEndsAt,
            product?.displayName,
            annualLimit,
        ],
    );

    const sessionIdsCountedTowardLimit = useMemo(() => {
        const counting = sessionsCountingTowardAnnualLimit(
            checkIns,
            GuidanceSessionType.QUICK_CHECK_IN,
            parseISO(membershipDetails.membershipYearEndsAt ?? ''),
        );
        return new Set(counting.map((session) => session.id));
    }, [checkIns, membershipDetails.membershipYearEndsAt]);

    const showCountsColumn = hasNumericLimit && checkIns.length > 0;

    const hasCompletedOnboardingSessions = useMemo(() => {
        return hasCompletedGuidanceOnboarding(guidanceSessions, product);
    }, [guidanceSessions, product]);

    // Check-ins are not relevant until onboarding sessions are complete
    if (!hasCompletedOnboardingSessions) {
        return null;
    }

    // Check-ins are not relevant if the current subscription product
    // has no check-in allowance and if there are no past check-ins.
    if (annualLimit === undefined && checkIns.length === 0) {
        return null;
    }

    return (
        <YStack gap={12} width="100%" marginBottom={48}>
            <XStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <XStack alignItems="center">
                    <H6Emphasized>Check-Ins by Membership Year</H6Emphasized>
                    <InfoButtonWithTooltip
                        tooltipContent="Past membership year ranges are based only on current membership year, not actual subscription history"
                        subtle
                    />
                </XStack>
                {rows.length > 0 && (
                    <CheckboxWithLabel
                        id="check-in-show-urls"
                        label="Show urls"
                        checked={showUrls}
                        onPress={() => {
                            setShowUrls((current) => !current);
                        }}
                    />
                )}
            </XStack>
            {rows.length === 0 ? (
                <BodyMediumRegular fontStyle="italic">
                    No check-in sessions
                </BodyMediumRegular>
            ) : (
                <DataTable
                    value={rows}
                    dataKey="id"
                    size="small"
                    tableStyle={{ width: '100%' }}
                    sortField="sortIndex"
                    sortOrder={1}
                    rowGroupMode="subheader"
                    groupRowsBy="yearKey"
                    rowGroupHeaderTemplate={(row: GroupedSessionRow) => (
                        <BodySmallEmphasized>
                            {row.yearLabel}
                        </BodySmallEmphasized>
                    )}
                    scrollHeight="285px"
                    scrollable
                >
                    {showCountsColumn && (
                        <Column
                            header="Counts Toward Limit"
                            style={{ width: '160px' }}
                            body={(row: GroupedSessionRow) => {
                                if (
                                    row.session == null ||
                                    !sessionIdsCountedTowardLimit.has(
                                        row.session.id,
                                    )
                                ) {
                                    return null;
                                }

                                return (
                                    <XStack paddingLeft={16}>
                                        <Icon
                                            icon={CircleCheckIcon}
                                            size={18}
                                            color="$green1100"
                                        />
                                    </XStack>
                                );
                            }}
                        />
                    )}
                    <Column
                        header="Status"
                        body={(row: GroupedSessionRow) => {
                            const session = row.session;
                            if (session == null) {
                                return (
                                    <BodyMediumRegular fontStyle="italic">
                                        none
                                    </BodyMediumRegular>
                                );
                            }

                            return (
                                <GuidanceSessionStatusCell
                                    status={session.status}
                                    startTime={session.startTime}
                                    endTime={session.endTime}
                                    autoCancelReason={session.autoCancelReason}
                                    confirmedAt={session.confirmedAt}
                                />
                            );
                        }}
                    />
                    <Column
                        header="Start Time"
                        body={(row: GroupedSessionRow) =>
                            row.session == null
                                ? null
                                : formatToSimpleDateTimeString(
                                      row.session.startTime,
                                      { weekday: 'short' },
                                  )
                        }
                    />
                    {showUrls && (
                        <Column
                            header="Video Conference Url"
                            body={(row: GroupedSessionRow) =>
                                row.session == null ? null : (
                                    <UrlLinkRenderer
                                        linkText="Zoom"
                                        url={row.session.videoConferenceUrl}
                                    />
                                )
                            }
                        />
                    )}
                    {showUrls && (
                        <Column
                            header="Reschedule Url"
                            body={(row: GroupedSessionRow) =>
                                row.session == null ? null : (
                                    <UrlLinkRenderer
                                        linkText="Reschedule"
                                        url={row.session.calendlyRescheduleUrl}
                                    />
                                )
                            }
                        />
                    )}
                    {showUrls && (
                        <Column
                            header="Cancel Url"
                            body={(row: GroupedSessionRow) =>
                                row.session == null ? null : (
                                    <UrlLinkRenderer
                                        linkText="Cancel"
                                        url={row.session.calendlyCancelUrl}
                                    />
                                )
                            }
                        />
                    )}
                    <Column
                        header="Notes"
                        align="center"
                        body={(row: GroupedSessionRow) => {
                            const session = row.session;
                            if (session == null) {
                                return null;
                            }

                            return (
                                <ViewItemRenderer
                                    icon={ExternalLinkIcon}
                                    onPress={() => {
                                        setNotesSession(session);
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        header="Transcript"
                        align="center"
                        body={(row: GroupedSessionRow) => {
                            if (row.session?.transcriptS3Key == null) {
                                return null;
                            }

                            const session = row.session;
                            return (
                                <ViewItemRenderer
                                    icon={CaptionsIcon}
                                    onPress={() => {
                                        setTranscriptSession(session);
                                    }}
                                />
                            );
                        }}
                    />
                </DataTable>
            )}
            {notesSession != null && (
                <GuidanceSessionNotesModal
                    membership={membershipDetails}
                    session={notesSession}
                    isOpen
                    onClose={() => {
                        setNotesSession(null);
                    }}
                />
            )}
            {transcriptSession != null && (
                <GuidanceSessionTranscriptModal
                    session={transcriptSession}
                    membershipDetails={membershipDetails}
                    isOpen
                    onClose={() => {
                        setTranscriptSession(null);
                    }}
                />
            )}
        </YStack>
    );
};
